import React, { Component, Fragment, SyntheticEvent } from "react";
import classnames from 'classnames';
import { connect } from 'react-redux';
import Interactive, { ClickType, State as ReactInteractiveState } from "react-interactive";

import { IArea } from "types/map";
import { openModalTooltip } from 'actions/modalTooltip';
import Tooltip from "./Tooltip";
import { areaToTooltipData, isLodgings } from "components/Map/util";
import MapContext from "components/Map/MapContext";

import { IAppState } from "types/app";
import { MAP_BASE_URL, PLAYER_MARKER_HEIGHT, PLAYER_MARKER_WIDTH } from "components/Map/constants";
import Buttonlet from "components/Buttonlet";
import Underline from 'components/Map/Lodgings/Underline';
import asStateAwareArea from 'mapping/asStateAwareArea';

export interface Props {
  area: IArea,
  areas?: IArea[],
  avatarImage: string,
  areLodgingsLocked: boolean,
  currentArea?: IArea,
  dispatch: Function,
  onClick: (_: any, area: IArea) => void,
  onSelect: (area?: IArea) => void,
  selectedArea: IArea | undefined,
}

interface State {
  isModalOpen: boolean,
  isTooltipActive: boolean,
}

export class LodgingsSubArea extends Component<Props, State> {
  ref = React.createRef<HTMLDivElement>();

  state = {
    isModalOpen: false,
    isTooltipActive: false,
  };

  handleClick = (e: SyntheticEvent<Element, Event>, clickType: ClickType) => {
    const { area, currentArea, onClick } = this.props;
    // Do nothing if we're in this area already
    if (area.id === currentArea?.id) {
      return;
    }

    // If we don't have an onClick, return early
    if (!onClick) {
      return;
    }

    // Iff this was a mouse click, run the onClick callback
    if (clickType === 'mouseClick') {
      onClick(e, area);
    }
  };

  handleOpenGateEvent = (_: any) => {
  };

  handleStateChange = ({ nextState, event }: { nextState: ReactInteractiveState, event: SyntheticEvent<Element, Event> }) => {
    const { area, areas, currentArea, dispatch, selectedArea, onClick, onSelect } = this.props;
    const tooltipData = areaToTooltipData(asStateAwareArea(area, areas || [], currentArea), currentArea, onClick);

    event.preventDefault(); // Don't propagate

    const { iState } = nextState;

    const isHovering = /hover/.test(iState);
    this.setState({ isTooltipActive: isHovering });
    if (isHovering) {
      onSelect(area);
    } else if (selectedArea === area) {
      onSelect();
    }

    // If we've been tapped, then show the modal
    if (/touchActive/.test(iState)) {
      dispatch(openModalTooltip({ ...tooltipData }));
    }
  };

  render() {
    const {
      area,
      areas,
      areLodgingsLocked,
      avatarImage,
      currentArea,
      onClick,
      selectedArea,
    } = this.props;

    if (areLodgingsLocked && !isLodgings(area)) {
      return null;
    }

    const { isTooltipActive } = this.state;

    const areWeHere = currentArea?.id === area.id;
    const isSelected = selectedArea?.areaKey === area.areaKey;

    return (
      <Fragment>
        <div
          className="lodgings-subarea"
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
            width: `${42 + 10 + 10}px`, // width of widest icon plus padding on either side
          }}
        >
          <Interactive
            as="div"
            className={classnames(
              'map__lodgings-button',
              areLodgingsLocked && 'map__lodgings-button--locked',
              areWeHere && 'map__lodgings-button--you-are-here',
            )}
            style={{
              bottom: '20px',
              right: 0,
            }}
            onClick={this.handleClick}
            onStateChange={this.handleStateChange}
          >
            <div
              ref={this.ref}
              style={{}}
            >
              <img
                alt={area.name}
                src={`${MAP_BASE_URL}/lodgings/${area.areaKey}.png`}
                className={classnames(
                  'map__lodgings-button__image',
                  areLodgingsLocked && 'map__lodgings-button__image--locked',
                )}
              />
              <img
                alt="player marker"
                src={`${MAP_BASE_URL}/playermarkers/${avatarImage}-player-marker.png`}
                height={PLAYER_MARKER_HEIGHT}
                width={PLAYER_MARKER_WIDTH}
                className={classnames(
                  'map__lodgings-button__player-marker',
                  'map__player-marker',
                  areWeHere && 'map__player-marker--visible',
                )}
              />
            </div>
          </Interactive>
          {areLodgingsLocked && isLodgings(area) && (
            <MapContext.Consumer>
              {({ onRequestOpenGateStoryletModal }) => (
                <div style={{}}>
                  <Buttonlet
                    onClick={() => onRequestOpenGateStoryletModal(area)}
                    type="padlock"
                  />
                </div>
              )}
            </MapContext.Consumer>
          )}
          {isSelected && <Underline />}
        </div>
        <Tooltip
          description={area.description}
          isTooltipActive={isTooltipActive}
          parent={this.ref.current!}
          tooltipData={areaToTooltipData(asStateAwareArea(area, areas || [], currentArea), currentArea, onClick, false)}
          tooltipPos="top"
        />
      </Fragment>
    );

  }
}

const mapStateToProps = ({
  myself: { character: { avatarImage } },
  map: { areas, currentArea },
}: IAppState) => ({
  areas,
  avatarImage,
  currentArea,
  areLodgingsLocked: !((areas || []).find(isLodgings)?.unlocked ?? false),
});

export default connect(mapStateToProps)(LodgingsSubArea);
